import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ChangePasswordPage } from './ChangePasswordPage';
import { FileUpload } from '../components/shared/FileUpload';
import { dataService } from '@/api';
import { useToast } from '../hooks/useToast';
import { PageContainer } from '../components/ui/PageContainer';
import { SectionCard } from '../components/ui/SectionCard';
import { Loader } from '../components/ui/Loader';
import { AppVersionLabel } from '../components/ui/AppVersionLabel';
import { MediaImage } from '../components/ui/MediaImage';

export function ProfilePage() {
  const { user, updateCurrentUser } = useAuth();
  const { showToast } = useToast();
  const [savingAvatar, setSavingAvatar] = useState(false);
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  const updateAvatar = async (avatarUrl) => {
    setSavingAvatar(true);
    try {
      await dataService.updateUser(user.id, { avatarUrl });
      updateCurrentUser({ avatarUrl });
      showToast('Profile picture updated', 'success');
    } catch (e) {
      showToast(e?.message || 'Failed to update profile picture', 'error');
    } finally {
      setSavingAvatar(false);
    }
  };
  const initials = user.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
  return (
    <PageContainer className="max-w-3xl mx-auto px-3 sm:px-0">
      <SectionCard className="max-w-xl mx-auto shadow-sm">
        <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">My Profile</h2>
        <p className="text-sm text-slate-500 font-medium mt-1">Account details and security</p>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
          {user.avatarUrl ? (
            <MediaImage
              src={user.avatarUrl}
              alt="Profile"
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover border border-slate-200 shadow-sm shrink-0"
            />
          ) : (
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-black text-xl border border-indigo-200 shrink-0">
              {initials || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-700">Profile picture</p>
            <p className="text-xs text-slate-500 mt-0.5">Shown across user lists and headers.</p>
          </div>
        </div>

        <div className="mt-5">
          <FileUpload
            preset="image"
            label="Upload profile picture"
            currentFile={user.avatarUrl || ''}
            onUpload={(r) => {
              void updateAvatar(r.url);
            }}
          />
          {savingAvatar ? (
            <div className="mt-3">
              <Loader variant="inline" size="sm" label="Saving…" />
            </div>
          ) : null}
        </div>

        <dl className="mt-6 text-sm text-slate-700 space-y-2 border-t border-slate-100 pt-6">
          <div className="flex flex-col sm:flex-row sm:gap-2">
            <dt className="font-bold text-slate-800 shrink-0 sm:w-24">Name</dt>
            <dd className="min-w-0 break-words">{user.name}</dd>
          </div>
          <div className="flex flex-col sm:flex-row sm:gap-2">
            <dt className="font-bold text-slate-800 shrink-0 sm:w-24">Email</dt>
            <dd className="min-w-0 break-all">{user.email}</dd>
          </div>
          <div className="flex flex-col sm:flex-row sm:gap-2">
            <dt className="font-bold text-slate-800 shrink-0 sm:w-24">Role</dt>
            <dd className="min-w-0">{user.role}</dd>
          </div>
        </dl>

        <div className="mt-6">
          <Link
            to={`/dashboard/${user.role}`}
            className="inline-flex text-sm font-bold text-indigo-600 hover:text-indigo-800 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 rounded-lg"
          >
            Back to dashboard
          </Link>
        </div>
      </SectionCard>

      <div className="pt-2">
        <ChangePasswordPage />
      </div>
      <AppVersionLabel align="center" className="mt-6" />
    </PageContainer>
  );
}
